import {
  ApiError,
  addSendingDomain,
  getSendingDomain,
  removeSendingDomain,
  verifySendingDomain,
} from '@/lib/api';
import type { SendingDomainState } from '@/lib/types';
import { AlertCircle, CheckCircle2, Loader2, Mail } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Spec §10.3 + §10.5 — Email sending-domain setup UI.
//
// State-machine renderer based on `state.status`:
//   - not_configured   → input + "Set up" button → POST.
//   - pending_dns      → DNS records table + "Verify now" + auto-poll 30s.
//   - pending_verifn   → spinner + auto-poll 30s.
//   - verified         → success line + "Change domain" → DELETE.
//   - failed           → reason + "Reset and try again" → DELETE.
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AddDomainForm({
  onAdded,
}: {
  onAdded: (state: SendingDomainState) => void;
}) {
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = domain.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      const state = await addSendingDomain(trimmed);
      onAdded(state);
    } catch (err) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div>
        <label
          htmlFor="sending-domain"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Sending domain
        </label>
        <input
          id="sending-domain"
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="mail.yourkennel.com"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          disabled={saving}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Use a subdomain you control, e.g. <span className="font-mono">mail.yourkennel.com</span>.
          Customer emails will be sent from this domain once it's verified.
        </p>
      </div>
      <button
        type="submit"
        disabled={saving || domain.trim().length === 0}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
      >
        {saving ? 'Setting up…' : 'Set up'}
      </button>
    </form>
  );
}

function ResetButton({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);

  function handleClick() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setRemoving(true);
    onRemove();
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={removing}
      className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-destructive hover:border-destructive press disabled:opacity-50"
    >
      {confirming ? 'Confirm' : label}
    </button>
  );
}

function DnsRecordTable({ records }: { records: SendingDomainState['dnsRecords'] }) {
  if (records.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Resend did not return any DNS records yet — try refreshing in a moment.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm font-mono border border-border rounded-md overflow-hidden">
        <thead>
          <tr className="bg-secondary text-muted-foreground text-[11px] uppercase tracking-wide">
            <th className="px-4 py-2 text-left font-medium">Type</th>
            <th className="px-4 py-2 text-left font-medium">Name</th>
            <th className="px-4 py-2 text-left font-medium">Value</th>
            <th className="px-4 py-2 text-left font-medium">TTL</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={`${r.type}-${r.name}-${i}`} className="border-t border-border align-top">
              <td className="px-4 py-2 whitespace-nowrap">{r.type}</td>
              <td className="px-4 py-2 break-all">{r.name}</td>
              <td className="px-4 py-2 break-all">{r.value}</td>
              <td className="px-4 py-2 num whitespace-nowrap">{r.ttl ?? 'Auto'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function EmailSendingDomain() {
  const [state, setState] = useState<SendingDomainState | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  // Track whether the most-recent load finished — used to decide if we should
  // schedule the next poll. Stored in a ref so polling logic doesn't trigger
  // re-renders.
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function load(silent = false) {
    if (!silent) setLoading(true);
    getSendingDomain()
      .then((s) => setState(s))
      .catch(() => {
        // Non-fatal — keep state.
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }

  useEffect(() => {
    load();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-poll every 30s while in a pending state.
  useEffect(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
    const status = state?.status;
    if (status === 'pending_dns' || status === 'pending_verification') {
      pollTimer.current = setTimeout(() => load(true), POLL_INTERVAL_MS);
    }
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [state?.status]);

  async function handleVerify() {
    setVerifying(true);
    try {
      const next = await verifySendingDomain();
      setState(next);
    } catch {
      // Non-fatal — refetch so we display the latest known state.
      load();
    } finally {
      setVerifying(false);
    }
  }

  async function handleRemove() {
    try {
      await removeSendingDomain();
    } catch {
      // Ignore — load() will fetch fresh state.
    }
    load();
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-[720px] px-6 py-10 md:px-10 md:py-14">
        <div className="space-y-3">
          <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
          <div className="h-4 w-full animate-pulse rounded bg-secondary" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-secondary" />
        </div>
      </div>
    );
  }

  const status = state?.status ?? 'not_configured';
  const domain = state?.domain ?? null;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Email sending domain</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Send customer-facing emails (booking confirmations, reminders, receipts) from a domain you
          own. Until this is verified, customer emails won't go out.
        </p>
      </header>

      <div className="space-y-6">
        {status === 'not_configured' && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Mail className="size-4 text-muted-foreground" strokeWidth={1.75} />
                Add sending domain
              </h2>
            </div>
            <div className="px-6 py-5">
              <AddDomainForm onAdded={(s) => setState(s)} />
            </div>
          </section>
        )}

        {status === 'pending_dns' && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Mail className="size-4 text-warning" strokeWidth={1.75} />
                Add these DNS records
              </h2>
            </div>
            <div className="px-6 py-5 space-y-5">
              <p className="text-sm text-muted-foreground">
                Add the following records to{' '}
                {domain ? (
                  <span className="font-mono font-medium text-foreground">{domain}</span>
                ) : (
                  'your DNS provider'
                )}
                , then click "Verify now". DNS changes can take a few minutes to propagate.
              </p>
              <DnsRecordTable records={state?.dnsRecords ?? []} />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void handleVerify()}
                  disabled={verifying}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                >
                  {verifying ? 'Verifying…' : "I've added the records — verify now"}
                </button>
                <ResetButton label="Reset" onRemove={() => void handleRemove()} />
              </div>
            </div>
          </section>
        )}

        {status === 'pending_verification' && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Loader2 className="size-4 text-warning animate-spin" strokeWidth={1.75} />
                Verifying DKIM
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-muted-foreground">
                We've found your DNS records for{' '}
                {domain && <span className="font-mono font-medium text-foreground">{domain}</span>}.
                Final DKIM verification typically takes 5–15 minutes — this page refreshes
                automatically.
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void handleVerify()}
                  disabled={verifying}
                  className="rounded-md border border-border px-3 py-1.5 text-sm press disabled:opacity-50"
                >
                  {verifying ? 'Checking…' : 'Check now'}
                </button>
                <ResetButton label="Reset" onRemove={() => void handleRemove()} />
              </div>
            </div>
          </section>
        )}

        {status === 'verified' && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="size-4 text-success" strokeWidth={1.75} />
                Sending domain verified
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-muted-foreground">
                {domain && (
                  <>
                    <span className="font-mono font-medium text-foreground">{domain}</span>{' '}
                  </>
                )}
                is verified. Customer-facing emails will be sent from this domain.
              </p>
              <ResetButton label="Change domain" onRemove={() => void handleRemove()} />
            </div>
          </section>
        )}

        {status === 'failed' && (
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-4">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <AlertCircle className="size-4 text-destructive" strokeWidth={1.75} />
                Verification failed
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              {state?.failureReason && (
                <p className="text-sm text-destructive">
                  Something went wrong: {state.failureReason}.
                </p>
              )}
              <ResetButton label="Reset and try again" onRemove={() => void handleRemove()} />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
